import React, { useContext, useRef, useState } from "react"
import { Text, View } from "react-native"
import IdentityInfoForm from "../utility/forms/identity-info"
import { textStyles } from "../style"
import { ThemeContext } from "../utility/context/ThemeContext"
import { ThemedButton } from "../utility/input/buttons"
import api from "../api-client"
import { useAppDispatch, useAppSelector } from '../store'
import { setUserField } from "../auth-slice"
import { useNavigation } from "@react-navigation/native"
import ThemedDialog from "../style/dialog"
import { Ionicons } from "@expo/vector-icons"
import KeyboardAvoidingScrollView from "../utility/keyboard-avoiding-scrollview"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { MAX_CONTENT_WIDTH } from "../config"
import { log } from "../utility"
import Analytics from "../utility/analytics"

const ManageDemographics = (): React.ReactElement => {
  const { user, loading } = useAppSelector((state) => state.auth)
  const theme = useContext(ThemeContext).theme
  const { textMain, bgMain } = theme.colors

  const dispatch = useAppDispatch()
  const navigation = useNavigation()

  const handleSubmitRef = useRef<() => void>(null)

  const [showSuccess, setShowSuccess] = useState(false)
  const [canSubmit, setCanSubmit] = useState(false)
  const [scrollViewHeight, setScrollViewHeight] = useState(0)

  const { bottom } = useSafeAreaInsets()
  const paddingTop = 16
  const paddingBottom = bottom + 24
  const contentMinHeight =
    scrollViewHeight > 0 ? scrollViewHeight - paddingTop - paddingBottom : undefined

  return (
    <View style={{ flex: 1, backgroundColor: bgMain }}>
      <KeyboardAvoidingScrollView
        style={{ flex: 1 }}
        extraScrollHeight={bottom * -2}
        onLayout={(event) => {
          setScrollViewHeight(event.nativeEvent.layout.height)
        }}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{
          flexGrow: 1,
          minHeight: scrollViewHeight > 0 ? scrollViewHeight : undefined,
          paddingTop,
          paddingHorizontal: 16,
          alignItems: 'center',
        }}
      >
        <View
          style={{
            flex: 1,
            width: '100%',
            maxWidth: MAX_CONTENT_WIDTH,
            minHeight: contentMinHeight,
            justifyContent: 'space-between',
          }}
        >
          <View style={{ gap: 10 }}>
            <Text style={[textStyles.body, { color: textMain }]}>
              Update your demographic details anytime. Your information is always yours to manage.
            </Text>
            <IdentityInfoForm
              needsModification
              onValidationSuccess={(handleSubmit) => {
                handleSubmitRef.current = handleSubmit
                setCanSubmit(true)
              }}
              onValidationFailure={() => {
                handleSubmitRef.current = undefined
                setCanSubmit(false)
              }}
              onSubmit={async (values) => {
                let succeeded = false
                try {
                  await api.updateUser({
                    identityInfo: {
                      ...user.identityInfo,
                      ...values,
                      genderDescription: values.gender === "Other (Please Specify)" ? values.genderDescription : "",
                      sexualOrientationDescription: values.sexualOrientation === "Other (Please Specify)" ? values.sexualOrientationDescription : "",
                    }
                  })

                  dispatch(setUserField({
                    field: "identityInfo",
                    value: {
                      ...user.identityInfo,
                      ...values,
                      genderDescription: values.gender === "Other (Please Specify)" ? values.genderDescription : "",
                      sexualOrientationDescription: values.sexualOrientation === "Other (Please Specify)" ? values.sexualOrientationDescription : "",
                    }
                  }))

                  setCanSubmit(false)
                  setShowSuccess(true)
                  succeeded = true
                } catch (error) {
                  log.error(error, { tags: { message: 'error updating demographics' } })
                } finally {
                  Analytics.track('update_demographics_submitted', {
                    success: succeeded,
                  })
                }
              }}
              loading={loading}
            />
          </View>
          <View style={{ gap: 10, paddingVertical: 12 }}>
            <ThemedButton
              text="Submit"
              onPress={() => {
                if (handleSubmitRef.current && canSubmit) {
                  handleSubmitRef.current()
                }
              }}
              disabled={loading || !canSubmit}
              variant="primary"
            />
            <ThemedButton
              text="Cancel"
              onPress={() => {
                Analytics.track('update_demographics_cancelled')
                navigation.goBack()
              }}
              variant="cancel"
            />
          </View>
        </View>
      </KeyboardAvoidingScrollView>
      <ThemedDialog
        title="Success"
        subMessage="Your demographic information has been updated successfully."
        options={[
          {
            title: "OK",
            onPress: () => {
              setShowSuccess(false)
              navigation.goBack()
            },
            icon: <Ionicons name="checkmark-circle" size={24} />,
            role: "confirm",
          }
        ]}
        visible={showSuccess}
      />
    </View>
  )
}

export default ManageDemographics